import geCountries from "@/json/global-e-countries.json";
import countryRegions from "@/json/country-regions.json";
import type { ShippingCountry } from "@/libs/getShippingCountries";
import {
  countryDisplayName,
  countryKoreanName,
  countrySearchText,
} from "@/libs/countries/country-names";

/**
 * GE 모드(`globalEEnabled`)의 국가 스위처 목록 — #5839.
 *
 * ## 누가 들어가나
 *
 * ```
 *   GE 가 운영하는 국가 (IsOperatedByGlobalE)   GE 가 국제 구간을 배송한다
 *   − AN                                       해체된 코드(아래)
 *   + KR                                       GE 미운영이지만 국내 결제(이니시스·네이버페이)로 판다
 * ```
 *
 * Kyuri(2026-09-29): *"계약서상 한국 제외한 모든 국가를 운영 … 실제 PROD 에서는 209개 국가"*.
 * sandbox `Browsing/Countries` 의 운영 국가 수가 정확히 209 였다(215개 중 미운영 6 =
 * BY · HT · KR · RU · SD · UA). ⚠️ **prod 목록은 아직 직접 받지 못했다** — prod GUID 가 우리 쪽에
 * 없다(Vercel env 미등록, 2026-09-29 확인). 캐시는 sandbox 기준이고, **플래그를 켜기 전에
 * prod 목록으로 캐시를 다시 생성해 대조**해야 한다.
 *
 * ## 왜 운영 여부로 거르나 — 목록에 있다고 파는 게 아니다
 *
 * GE 응답에는 미운영국도 들어온다(BY · RU · UA …). 그걸 보여 주면 고를 수는 있는데 GE 가
 * 받지 않는다. 비배송국이 commerce country 로 박혀 freight $0 로 결제된 사고가 이미 있었다
 * (`middleware.ts` 주석, 에콰도르 2건).
 *
 * ## AN 을 빼는 이유
 *
 * 네덜란드령 안틸레스는 2010년에 해체됐고 ISO 3166 에서도 2011년에 코드가 지워졌다. GE 목록에는
 * `AN` 과 후속 코드(`CW` · `SX` · `BQ`)가 **같이** 있어 같은 지역이 두 번 뜬다. 게다가 Intl 은
 * `AN` 을 `Curaçao` 로 적어 목록에 `Curaçao` 가 두 개 생긴다.
 *
 * ## KR 을 넣는 이유
 *
 * KR 은 GE 미운영(`operated: false`)이지만 스위처에서 빠지면 **한국 사용자가 KR 을 고를 수 없다**
 * — 원화 가격과 국내 결제가 KR 선택에 걸려 있다. GE 로 보내지 않는 것은 `isGeRoutedFor` 의
 * `cc !== "KR"` 가 따로 지킨다.
 */

type GeCountryInfo = {
  currency: string;
  operated: boolean;
  fixedPrice: boolean;
};

const GE = geCountries as Record<string, GeCountryInfo>;
const REGIONS = countryRegions as Record<string, string>;

/** GE 운영국이어도 목록에 올리지 않는 코드. 늘릴 때는 이유를 위 docblock 에 같이 적는다. */
const EXCLUDED = new Set(["AN"]);

/** GE 미운영이어도 목록에 올리는 코드. */
const ALWAYS_INCLUDED = ["KR"];

/**
 * 표시 대상 국가 코드. 순서는 의미 없다(정렬은 소비처가 한다).
 *
 * ⚠️ **지역이 없다고 여기서 거르지 않는다.** 한 번 걸렀더니 그 필터가 `AN` 제외를 **대신**
 * 해 주고 있었다(`AN` 은 M49 에 없어 지역이 없다) — `EXCLUDED` 에서 `AN` 을 지워도 테스트가
 * 초록이었다(2026-09-29 뮤턴트 실측). 지역 누락은 조용히 없애지 말고 드러나야 한다 →
 * 가드(`__tests__/libs/countries`)가 **모든 행이 지역을 갖는다**를 잠근다.
 */
export function geShippingCountryCodes(): string[] {
  const operated = Object.keys(GE).filter(
    (cc) => GE[cc].operated && !EXCLUDED.has(cc),
  );
  return [...new Set([...operated, ...ALWAYS_INCLUDED])];
}

/** `useShippingCountries` 가 돌려주는 모양과 같게 만든다 — 소비처 5곳이 그대로 읽는다. */
export function geShippingCountries(): ShippingCountry[] {
  return geShippingCountryCodes().map((cc) => {
    const name = countryDisplayName(cc);
    return {
      countryCode: cc,
      englishCountryName: name,
      ownCountryName: name,
      koreanCountryName: countryKoreanName(cc),
      region: REGIONS[cc],
      // GE 응답엔 국가번호·지원언어가 없다. 두 필드를 라이브러리 밖에서 읽는 곳은 없다
      // (2026-09-29 grep). ⚠️ 다만 국가번호는 **검색어**로 쓰였으므로 GE 모드에서는
      // `82` · `+1` 같은 국가번호 검색이 **안 된다** — 기능이 줄어든 것이다(#5842 R1 Nit).
      // mgmt 경로는 옛 `searchText` 를 넘겨 살리지만 GE 모드는 mgmt 를 부르지 않아 되살릴
      // 데이터가 없다. 되살리려면 국가번호 표를 따로 둬야 한다.
      countryCallingCodes: [],
      supportedLanguages: [],
      searchText: countrySearchText(cc),
    };
  });
}
